import { useEffect, useRef, useState } from 'preact/hooks'
import type {
  ArticleCatalogue,
  ConfigurationCatalogue,
} from '../../serveur/src/catalogues/index.ts'

type Catalogue = {
  id: string
  version_id: string
  numero: number
  catalogue: ConfigurationCatalogue
  publie: boolean
  modifications_en_attente: boolean
  url_publique: string | null
  par_defaut: boolean
  maj_le_utc: string
  manques?: string[]
}
type Edition = {
  id?: string
  version_id?: string
  numero?: number
  catalogue: ConfigurationCatalogue
  initial: string
  publie?: boolean
  url_publique?: string | null
}
const uid = () => crypto.randomUUID()
const argent = (n: number) =>
  new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(
    n / 100,
  )
const unite: Record<string, string> = {
  heure: 'h',
  jour: 'jour',
  reservation: 'réservation',
  personne: 'personne',
  unite: 'unité',
}
const typeNom: Record<string, string> = {
  espace: 'Espace',
  equipement: 'Équipement',
  produit: 'Produit',
  activite: 'Activité',
}
const slugDe = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100)
function deplacer<T>(liste: T[], de: number, vers: number) {
  if (vers < 0 || vers >= liste.length) return liste
  const copie = [...liste],
    item = copie.splice(de, 1)[0]!
  copie.splice(vers, 0, item)
  return copie
}
const initial = (): ConfigurationCatalogue => ({
  nom: '',
  slug: '',
  titre: 'Trouvez l’espace pour votre prochain projet.',
  introduction:
    'Des espaces et des équipements pour donner vie à vos idées. Choisissez ce qu’il vous faut, puis laissez-vous guider.',
  couleur: '#391d44',
  disposition: 'cartes',
  afficher_recherche: true,
  par_defaut: false,
  sections: [{ id: uid(), nom: 'Nos espaces', description: '', elements: [] }],
})

export function CataloguesPane({
  baseUrl = '',
  permissions,
  onAuth,
  visible = true,
}: {
  baseUrl: string
  permissions: string[]
  onAuth?: () => void
  visible?: boolean
}) {
  const [catalogues, setCatalogues] = useState<Catalogue[]>([]),
    [items, setItems] = useState<ArticleCatalogue[]>([]),
    [charge, setCharge] = useState(false)
  const [edition, setEdition] = useState<Edition | null>(null),
    [liste, setListe] = useState(true),
    [onglet, setOnglet] = useState<'contenu' | 'apparence' | 'publication'>(
      'contenu',
    ),
    [selection, setSelection] = useState('')
  const [erreur, setErreur] = useState(''),
    [message, setMessage] = useState(''),
    [occupe, setOccupe] = useState(false)
  const [recherche, setRecherche] = useState(''),
    [choix, setChoix] = useState<string[] | null>(null),
    [filtreItems, setFiltreItems] = useState(''),
    [apercu, setApercu] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<{
    titre: string
    message: string
    action: () => void
    danger?: boolean
    label: string
  } | null>(null)
  const dialogue = useRef<HTMLDialogElement>(null),
    dialogueApercu = useRef<HTMLDialogElement>(null),
    dialogueConfirmation = useRef<HTMLDialogElement>(null),
    drag = useRef<{ type: 'section' | 'item'; index: number } | null>(null)
  const modifiable = permissions.includes('services.modifier'),
    modifie = Boolean(
      edition && JSON.stringify(edition.catalogue) !== edition.initial,
    ),
    c = edition?.catalogue,
    section = c?.sections.find((s) => s.id === selection)
  async function api(path = '', method = 'GET', body?: unknown) {
    const r = await fetch(`${baseUrl}/api/admin/catalogues${path}`, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'content-type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    })
    if (r.status === 401) onAuth?.()
    const data = await r.json()
    if (!r.ok)
      throw new Error(
        data.erreur?.message ?? 'Impossible de charger les catalogues.',
      )
    return data
  }
  async function charger() {
    try {
      const d = await api()
      setCatalogues(d.catalogues)
      setItems(d.items)
      setCharge(true)
    } catch (e) {
      setErreur(String((e as Error).message))
    }
  }
  useEffect(() => {
    if (visible) void charger()
    else {
      setChoix(null)
      setApercu(null)
      setConfirmation(null)
    }
  }, [visible])
  useEffect(() => {
    if (choix !== null && !dialogue.current?.open) dialogue.current?.showModal()
  }, [choix !== null])
  useEffect(() => {
    if (apercu && !dialogueApercu.current?.open)
      dialogueApercu.current?.showModal()
  }, [apercu])
  useEffect(() => {
    if (confirmation && !dialogueConfirmation.current?.open)
      dialogueConfirmation.current?.showModal()
  }, [confirmation])
  useEffect(() => {
    if (!modifie) return
    const avertir = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', avertir)
    return () => window.removeEventListener('beforeunload', avertir)
  }, [modifie])
  async function agir(f: () => Promise<void>) {
    setOccupe(true)
    setErreur('')
    setMessage('')
    try {
      await f()
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupe(false)
    }
  }
  function changer(p: Partial<ConfigurationCatalogue>) {
    setEdition((v) => (v ? { ...v, catalogue: { ...v.catalogue, ...p } } : v))
    setMessage('')
  }
  function changerSection(
    p: Partial<ConfigurationCatalogue['sections'][number]>,
  ) {
    if (!c || !section) return
    changer({
      sections: c.sections.map((s) =>
        s.id === section.id ? { ...s, ...p } : s,
      ),
    })
  }
  function ouvrir(v: Catalogue) {
    setEdition({ ...v, initial: JSON.stringify(v.catalogue) })
    setSelection(v.catalogue.sections[0]?.id ?? '')
    setListe(false)
    setOnglet('contenu')
    setErreur('')
    setMessage('')
  }
  function nouveau() {
    const action = () => {
      const d = initial()
      setEdition({ catalogue: d, initial: '' })
      setSelection(d.sections[0]!.id)
      setListe(false)
      setOnglet('apparence')
      setErreur('')
      setMessage('')
    }
    if (modifie)
      setConfirmation({
        titre: 'Créer un autre catalogue ?',
        message:
          'Votre brouillon ouvert contient des modifications non enregistrées. Enregistrez-les avant de créer un autre catalogue, ou abandonnez-les.',
        label: 'Abandonner et créer',
        danger: true,
        action,
      })
    else action()
  }
  function choisirCatalogue(v: Catalogue) {
    if (modifie && edition?.id !== v.id)
      setConfirmation({
        titre: 'Ouvrir un autre catalogue ?',
        message:
          'Les modifications non enregistrées du catalogue ouvert seront abandonnées.',
        label: 'Abandonner et ouvrir',
        danger: true,
        action: () => ouvrir(v),
      })
    else if (modifie && edition?.id === v.id) setListe(false)
    else ouvrir(v)
  }
  async function sauvegarder(): Promise<Catalogue> {
    if (!edition) throw new Error('Ouvrez un catalogue.')
    const d = (await api(
      edition.id ? `/${edition.id}` : '',
      edition.id ? 'PUT' : 'POST',
      { catalogue: edition.catalogue, version_id: edition.version_id },
    )) as Catalogue
    setEdition({ ...d, initial: JSON.stringify(d.catalogue) })
    await charger()
    return d
  }
  async function voirApercu() {
    await agir(async () => {
      const d = modifie || !edition?.id ? await sauvegarder() : edition!
      const r = await fetch(`${baseUrl}/api/admin/catalogues/${d.id}/apercu`, {
        credentials: 'same-origin',
      })
      if (r.status === 401) onAuth?.()
      if (!r.ok) throw new Error('Impossible de préparer l’aperçu.')
      setApercu(await r.text())
    })
  }
  async function publier() {
    await agir(async () => {
      const d = modifie || !edition?.id ? await sauvegarder() : edition!
      const resultat = (await api(`/${d.id}/publier`, 'POST', {
        version_id: d.version_id,
      })) as Catalogue
      setEdition({ ...resultat, initial: JSON.stringify(resultat.catalogue) })
      await charger()
      setMessage(
        'Le catalogue est publié. Les visiteurs voient maintenant cette version.',
      )
    })
  }
  function ajouterSection() {
    if (!c) return
    const s = {
      id: uid(),
      nom: 'Nouvelle catégorie',
      description: '',
      elements: [],
    }
    changer({ sections: [...c.sections, s] })
    setSelection(s.id)
  }
  function retirerSection() {
    if (!c || !section) return
    setConfirmation({
      titre: `Retirer « ${section.nom || 'cette catégorie'} » ?`,
      message:
        'Ses articles restent dans l’inventaire. Seule cette catégorie du catalogue sera retirée.',
      label: 'Retirer la catégorie',
      danger: true,
      action: () => {
        changer({ sections: c.sections.filter((s) => s.id !== section.id) })
        setSelection(c.sections.find((s) => s.id !== section.id)?.id ?? '')
      },
    })
  }
  function ajouterChoix() {
    if (!section || choix === null) return
    changerSection({
      elements: [
        ...section.elements,
        ...choix
          .filter((id) => !section.elements.some((e) => e.item_id === id))
          .map((id) => ({
            item_id: id,
            service_cle: items.find((i) => i.id === id)?.parcours[0]?.cle ?? '',
          })),
      ],
    })
    setChoix(null)
    setFiltreItems('')
  }
  const manques = c
    ? [
        ...(!c.nom.trim() ? ['Donnez un nom à votre catalogue.'] : []),
        ...(!c.slug ? ['Choisissez son adresse.'] : []),
        ...(!c.titre.trim() ? ['Ajoutez un titre à la vitrine.'] : []),
        ...(!c.sections.length ? ['Ajoutez une catégorie.'] : []),
        ...c.sections.flatMap((s) => [
          ...(!s.nom.trim() ? ['Une catégorie attend son nom.'] : []),
          ...(!s.elements.length ? [`Ajoutez un élément à « ${s.nom} ».`] : []),
          ...s.elements.flatMap((e) => {
            const i = items.find((i) => i.id === e.item_id)
            return !i?.actif
              ? [`Un article de « ${s.nom} » a été retiré de l’inventaire.`]
              : !i.parcours.some((p) => p.cle === e.service_cle)
                ? [`Choisissez un parcours publié pour « ${i.nom} ».`]
                : []
          }),
        ]),
      ]
    : []
  const nbArticles = c?.sections.reduce((n, s) => n + s.elements.length, 0) ?? 0
  return (
    <div class="cat-admin">
      <style>{stylesCatalogues}</style>
      {erreur && (
        <div class="cat-notice cat-erreur" role="alert">
          {erreur}
          <button
            type="button"
            onClick={() => setErreur('')}
            aria-label="Fermer le message"
          >
            ×
          </button>
        </div>
      )}
      {message && (
        <div class="cat-notice" role="status">
          {message}
        </div>
      )}
      {liste ? (
        <>
          <div class="cat-entete">
            <div>
              <p class="cat-surtitre">Catalogue de réservation</p>
              <h2>Des offres faciles à découvrir.</h2>
              <p>
                Regroupez vos éléments et composez les menus que vos visiteurs
                verront.
              </p>
            </div>
            {modifiable && (
              <button class="cat-primaire" onClick={nouveau}>
                ＋ Créer un catalogue
              </button>
            )}
          </div>
          {edition && modifie && (
            <div class="cat-reprendre">
              <span>Votre catalogue en cours est conservé.</span>
              <button onClick={() => setListe(false)}>
                Reprendre le brouillon →
              </button>
            </div>
          )}
          <div class="cat-barre">
            <label class="cat-recherche">
              <span class="cat-sr">Rechercher un catalogue</span>
              <input
                type="search"
                placeholder="Rechercher un catalogue"
                value={recherche}
                onInput={(e) => setRecherche(e.currentTarget.value)}
              />
            </label>
            <span>
              {catalogues.length} catalogue{catalogues.length > 1 ? 's' : ''}
            </span>
          </div>
          {!charge && !erreur ? (
            <p role="status">Chargement des catalogues…</p>
          ) : catalogues.length === 0 ? (
            <div class="cat-vide">
              <span class="cat-vide-icone" aria-hidden="true">
                ▦
              </span>
              <h3>Votre vitrine commence ici.</h3>
              <p>
                Choisissez des éléments de l’inventaire, rangez-les en
                catégories et donnez votre style à leur présentation.
              </p>
              {modifiable && (
                <button class="cat-primaire" onClick={nouveau}>
                  Créer mon premier catalogue
                </button>
              )}
            </div>
          ) : (
            <div class="cat-cartes">
              {catalogues
                .filter((v) =>
                  v.catalogue.nom
                    .toLowerCase()
                    .includes(recherche.toLowerCase()),
                )
                .map((v) => (
                  <button
                    class="cat-carte"
                    key={v.id}
                    onClick={() => choisirCatalogue(v)}
                  >
                    <div
                      class="cat-miniature"
                      style={{ '--cat-couleur': v.catalogue.couleur }}
                    >
                      <span />
                      <div>
                        <i />
                        <i />
                        <i />
                      </div>
                    </div>
                    <div class="cat-carte-info">
                      <div class="cat-etiquettes">
                        <span
                          class={
                            v.publie ? 'cat-badge cat-publie' : 'cat-badge'
                          }
                        >
                          {v.publie ? 'Publié' : 'Brouillon'}
                        </span>
                        {v.par_defaut && (
                          <span class="cat-badge">Page d’accueil</span>
                        )}
                        {v.modifications_en_attente && (
                          <span class="cat-badge">
                            Modifications en attente
                          </span>
                        )}
                      </div>
                      <h3>{v.catalogue.nom}</h3>
                      <p>
                        {v.catalogue.sections.length} catégorie
                        {v.catalogue.sections.length > 1 ? 's' : ''} ·{' '}
                        {v.catalogue.sections.reduce(
                          (n, s) => n + s.elements.length,
                          0,
                        )}{' '}
                        articles
                      </p>
                      <span class="cat-ouvrir">
                        Ouvrir le catalogue <span aria-hidden="true">→</span>
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </>
      ) : edition && c ? (
        <>
          <div class="cat-edition-entete">
            <div>
              <button class="cat-retour" onClick={() => setListe(true)}>
                ← Catalogues
              </button>
              <h2>{c.nom || 'Nouveau catalogue'}</h2>
              <p>
                {edition.publie
                  ? 'Version publique disponible'
                  : 'Brouillon privé'}
                {modifie
                  ? ' · Modifications à enregistrer'
                  : edition.numero
                    ? ` · Version ${edition.numero}`
                    : ''}
              </p>
            </div>
            <div class="cat-actions">
              <button
                disabled={occupe || !c.nom.trim() || !c.slug}
                onClick={() => void voirApercu()}
              >
                Aperçu
              </button>
              {modifiable && (
                <button
                  class="cat-primaire"
                  disabled={
                    occupe || !modifie || c.nom.trim().length < 2 || !c.slug
                  }
                  onClick={() =>
                    void agir(async () => {
                      await sauvegarder()
                      setMessage(
                        'Brouillon enregistré. La version publique reste celle de la dernière publication.',
                      )
                    })
                  }
                >
                  {occupe ? 'En cours…' : 'Enregistrer'}
                </button>
              )}
            </div>
          </div>
          <nav class="cat-onglets" aria-label="Configurer le catalogue">
            {(
              [
                ['contenu', 'Éléments et catégories'],
                ['apparence', 'Apparence'],
                ['publication', 'Publication'],
              ] as const
            ).map(([id, nom]) => (
              <button
                aria-current={onglet === id ? 'page' : undefined}
                onClick={() => setOnglet(id)}
              >
                {nom}
                {id === 'publication' && manques.length > 0 && (
                  <span>{manques.length}</span>
                )}
              </button>
            ))}
          </nav>
          {onglet === 'contenu' ? (
            <div class="cat-composition">
              <aside class="cat-categories">
                <div class="cat-panneau-titre">
                  <h3>Le menu de votre vitrine</h3>
                  <p>Glissez les catégories pour changer leur ordre.</p>
                </div>
                <div class="cat-section-liste">
                  {c.sections.map((s, index) => (
                    <div
                      key={s.id}
                      class={`cat-section-choix ${s.id === selection ? 'active' : ''}`}
                      draggable={modifiable}
                      onDragStart={() => {
                        drag.current = { type: 'section', index }
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault()
                        if (drag.current?.type === 'section')
                          changer({
                            sections: deplacer(
                              c.sections,
                              drag.current.index,
                              index,
                            ),
                          })
                        drag.current = null
                      }}
                    >
                      <button
                        class="cat-section-ouvrir"
                        onClick={() => setSelection(s.id)}
                      >
                        <span aria-hidden="true">⠿</span>
                        <span>
                          {s.nom || 'Sans nom'}
                          <small>
                            {s.elements.length} article
                            {s.elements.length > 1 ? 's' : ''}
                          </small>
                        </span>
                      </button>
                      {modifiable && (
                        <div class="cat-deplacer">
                          <button
                            aria-label={`Monter la catégorie ${s.nom}`}
                            disabled={index === 0}
                            onClick={() =>
                              changer({
                                sections: deplacer(
                                  c.sections,
                                  index,
                                  index - 1,
                                ),
                              })
                            }
                          >
                            ↑
                          </button>
                          <button
                            aria-label={`Descendre la catégorie ${s.nom}`}
                            disabled={index === c.sections.length - 1}
                            onClick={() =>
                              changer({
                                sections: deplacer(
                                  c.sections,
                                  index,
                                  index + 1,
                                ),
                              })
                            }
                          >
                            ↓
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {modifiable && (
                  <button class="cat-ajouter-section" onClick={ajouterSection}>
                    ＋ Ajouter une catégorie
                  </button>
                )}
                <p class="cat-note">
                  Un article peut apparaître dans plusieurs catégories. Son
                  stock et son parcours restent partagés.
                </p>
              </aside>
              <section class="cat-section-editeur">
                {section ? (
                  <>
                    <div class="cat-section-champs">
                      <label>
                        Nom de la catégorie
                        <input
                          maxLength={120}
                          value={section.nom}
                          disabled={!modifiable}
                          onInput={(e) =>
                            changerSection({ nom: e.currentTarget.value })
                          }
                          placeholder="Ex. Salles de réunion"
                        />
                      </label>
                      <label>
                        Description <span>facultative</span>
                        <textarea
                          rows={2}
                          maxLength={600}
                          value={section.description}
                          disabled={!modifiable}
                          onInput={(e) =>
                            changerSection({
                              description: e.currentTarget.value,
                            })
                          }
                          placeholder="Aidez les visiteurs à choisir."
                        />
                      </label>
                    </div>
                    <div class="cat-sous-entete">
                      <div>
                        <h3>Articles de cette catégorie</h3>
                        <p>
                          Le parcours choisi s’ouvre lorsque le client
                          sélectionne l’élément.
                        </p>
                      </div>
                      {modifiable && (
                        <button
                          onClick={() => {
                            setChoix([])
                            setFiltreItems('')
                          }}
                        >
                          ＋ Ajouter des éléments
                        </button>
                      )}
                    </div>
                    {!section.elements.length ? (
                      <div class="cat-vide cat-vide-petit">
                        <h3>Quels articles souhaitez-vous présenter ?</h3>
                        <p>
                          Choisissez des espaces, équipements, activités ou
                          produits de votre inventaire.
                        </p>
                        {modifiable && (
                          <button
                            onClick={() => {
                              setChoix([])
                              setFiltreItems('')
                            }}
                          >
                            Choisir dans l’inventaire
                          </button>
                        )}
                      </div>
                    ) : (
                      <div class="cat-items">
                        {section.elements.map((e, index) => {
                          const item = items.find((i) => i.id === e.item_id),
                            parcours = item?.parcours.find(
                              (p) => p.cle === e.service_cle,
                            )
                          return (
                            <article
                              class="cat-item"
                              key={e.item_id}
                              draggable={modifiable}
                              onDragStart={() => {
                                drag.current = { type: 'item', index }
                              }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(event) => {
                                event.preventDefault()
                                if (drag.current?.type === 'item')
                                  changerSection({
                                    elements: deplacer(
                                      section.elements,
                                      drag.current.index,
                                      index,
                                    ),
                                  })
                                drag.current = null
                              }}
                            >
                              <div class="cat-item-texte">
                                <span class="cat-badge">
                                  {typeNom[item?.type ?? ''] ?? 'Article'}
                                </span>
                                <h4>{item?.nom ?? 'Article retiré'}</h4>
                                <p>
                                  {parcours
                                    ? `${parcours.prix_cents === 0 ? 'Gratuit' : argent(parcours.prix_cents) + ' / ' + (unite[parcours.unite] ?? parcours.unite)}`
                                    : 'Parcours à relier'}
                                </p>
                              </div>
                              <label class="cat-parcours">
                                Parcours client
                                <select
                                  value={e.service_cle}
                                  disabled={!modifiable}
                                  onChange={(event) =>
                                    changerSection({
                                      elements: section.elements.map((v, i) =>
                                        i === index
                                          ? {
                                              ...v,
                                              service_cle:
                                                event.currentTarget.value,
                                            }
                                          : v,
                                      ),
                                    })
                                  }
                                >
                                  <option value="">
                                    Choisir un parcours publié
                                  </option>
                                  {item?.parcours.map((p) => (
                                    <option key={p.cle} value={p.cle}>
                                      {p.nom}
                                    </option>
                                  ))}
                                </select>
                                {!item?.actif ? (
                                  <small class="cat-texte-erreur">
                                    Cet élément est inactif.
                                  </small>
                                ) : (
                                  !parcours && (
                                    <small class="cat-texte-erreur">
                                      Publiez un parcours lié à cet élément dans
                                      Parcours client.
                                    </small>
                                  )
                                )}
                              </label>
                              {modifiable && (
                                <div class="cat-item-actions">
                                  <button
                                    aria-label={`Monter ${item?.nom ?? 'l’élément'}`}
                                    disabled={index === 0}
                                    onClick={() =>
                                      changerSection({
                                        elements: deplacer(
                                          section.elements,
                                          index,
                                          index - 1,
                                        ),
                                      })
                                    }
                                  >
                                    ↑
                                  </button>
                                  <button
                                    aria-label={`Descendre ${item?.nom ?? 'l’élément'}`}
                                    disabled={
                                      index === section.elements.length - 1
                                    }
                                    onClick={() =>
                                      changerSection({
                                        elements: deplacer(
                                          section.elements,
                                          index,
                                          index + 1,
                                        ),
                                      })
                                    }
                                  >
                                    ↓
                                  </button>
                                  <button
                                    aria-label={`Retirer ${item?.nom ?? 'l’élément'} de la catégorie`}
                                    onClick={() =>
                                      changerSection({
                                        elements: section.elements.filter(
                                          (_, i) => i !== index,
                                        ),
                                      })
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                            </article>
                          )
                        })}
                      </div>
                    )}
                    {modifiable && (
                      <button class="cat-supprimer" onClick={retirerSection}>
                        Retirer cette catégorie
                      </button>
                    )}
                  </>
                ) : (
                  <div class="cat-vide">
                    <h3>Organisez votre catalogue.</h3>
                    <p>
                      Une catégorie regroupe les éléments qui vont bien
                      ensemble.
                    </p>
                    {modifiable && (
                      <button onClick={ajouterSection}>
                        Ajouter une catégorie
                      </button>
                    )}
                  </div>
                )}
              </section>
            </div>
          ) : onglet === 'apparence' ? (
            <div class="cat-apparence">
              <div class="cat-reglages">
                <div class="cat-panneau-titre">
                  <h3>Une vitrine à votre image</h3>
                  <p>
                    Les titres, couleurs et menus sont propres à chaque
                    catalogue.
                  </p>
                </div>
                <label>
                  Nom du catalogue
                  <input
                    value={c.nom}
                    maxLength={120}
                    disabled={!modifiable}
                    onInput={(e) => {
                      const nom = e.currentTarget.value
                      changer({
                        nom,
                        ...(!edition.id && (!c.slug || c.slug === slugDe(c.nom))
                          ? { slug: slugDe(nom) }
                          : {}),
                      })
                    }}
                    placeholder="Ex. Espaces et équipements"
                  />
                  <small>Ce nom apparaît en haut de votre vitrine.</small>
                </label>
                <label>
                  Titre d’accueil
                  <input
                    value={c.titre}
                    maxLength={180}
                    disabled={!modifiable}
                    onInput={(e) => changer({ titre: e.currentTarget.value })}
                  />
                </label>
                <label>
                  Texte d’introduction
                  <textarea
                    value={c.introduction}
                    rows={4}
                    maxLength={2000}
                    disabled={!modifiable}
                    onInput={(e) =>
                      changer({ introduction: e.currentTarget.value })
                    }
                  />
                </label>
                <div class="cat-couleur">
                  <label>
                    Couleur d’accent
                    <input
                      type="color"
                      value={c.couleur}
                      disabled={!modifiable}
                      onInput={(e) =>
                        changer({ couleur: e.currentTarget.value })
                      }
                    />
                  </label>
                  <div class="cat-palettes">
                    {[
                      '#391d44',
                      '#315e53',
                      '#31527c',
                      '#7a493b',
                      '#242c32',
                    ].map((couleur) => (
                      <button
                        style={{ background: couleur }}
                        disabled={!modifiable}
                        aria-label={`Couleur ${couleur}`}
                        aria-pressed={c.couleur === couleur}
                        onClick={() => changer({ couleur })}
                      />
                    ))}
                  </div>
                </div>
                <fieldset class="cat-disposition">
                  <legend>Présentation des éléments</legend>
                  <label>
                    <input
                      type="radio"
                      name="cat-disposition"
                      checked={c.disposition === 'cartes'}
                      disabled={!modifiable}
                      onChange={() => changer({ disposition: 'cartes' })}
                    />
                    <span>▦</span>
                    <strong>Cartes</strong>
                    <small>Pour explorer d’un coup d’œil</small>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="cat-disposition"
                      checked={c.disposition === 'liste'}
                      disabled={!modifiable}
                      onChange={() => changer({ disposition: 'liste' })}
                    />
                    <span>☷</span>
                    <strong>Liste</strong>
                    <small>Pour parcourir une longue offre</small>
                  </label>
                </fieldset>
                <label class="cat-checkbox">
                  <input
                    type="checkbox"
                    checked={c.afficher_recherche}
                    disabled={!modifiable}
                    onChange={(e) =>
                      changer({ afficher_recherche: e.currentTarget.checked })
                    }
                  />
                  <span>Afficher la recherche dans le catalogue</span>
                </label>
              </div>
              <div class="cat-preview-wrap">
                <div class="cat-preview-entete">
                  <span>Aperçu de la présentation</span>
                  <button
                    disabled={occupe || !c.nom.trim() || !c.slug}
                    onClick={() => void voirApercu()}
                  >
                    Ouvrir en grand ↗
                  </button>
                </div>
                <div class="cat-preview" style={{ '--cat-couleur': c.couleur }}>
                  <span class="cat-preview-marque">Votre organisation</span>
                  <p class="cat-preview-sur">{c.nom || 'Nom du catalogue'}</p>
                  <h3>{c.titre || 'Votre titre d’accueil'}</h3>
                  <p>{c.introduction}</p>
                  <div class="cat-preview-menu">
                    {c.sections.map((s) => (
                      <span>{s.nom || 'Catégorie'}</span>
                    ))}
                  </div>
                  {c.afficher_recherche && (
                    <div class="cat-preview-recherche">
                      ⌕ Rechercher un espace, un équipement…
                    </div>
                  )}
                  {c.sections.slice(0, 3).map((s) => (
                    <section>
                      <h4>{s.nom || 'Catégorie'}</h4>
                      {s.description && <p>{s.description}</p>}
                      <div
                        class={`cat-preview-items ${c.disposition === 'liste' ? 'cat-preview-liste' : ''}`}
                      >
                        {s.elements.length ? (
                          s.elements.slice(0, 6).map((e) => {
                            const i = items.find((i) => i.id === e.item_id),
                              p = i?.parcours.find(
                                (p) => p.cle === e.service_cle,
                              )
                            return (
                              <article>
                                <small>
                                  {typeNom[i?.type ?? ''] ?? 'Article'}
                                </small>
                                <h5>{i?.nom ?? 'Article'}</h5>
                                <p>{i?.description}</p>
                                <strong>
                                  {p
                                    ? p.prix_cents === 0
                                      ? 'Gratuit'
                                      : argent(p.prix_cents) +
                                        ' / ' +
                                        (unite[p.unite] ?? p.unite)
                                    : 'Parcours à relier'}
                                </strong>
                                <span>Découvrir →</span>
                              </article>
                            )
                          })
                        ) : (
                          <div class="cat-preview-placeholder">
                            Vos articles apparaîtront ici.
                          </div>
                        )}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div class="cat-publication">
              <div class="cat-reglages">
                <div class="cat-panneau-titre">
                  <h3>Ouvrir votre vitrine</h3>
                  <p>
                    Enregistrer conserve votre travail. Publier le rend visible
                    aux clients.
                  </p>
                </div>
                <label>
                  Nom du catalogue
                  <input
                    value={c.nom}
                    maxLength={120}
                    disabled={!modifiable}
                    onInput={(e) => {
                      const nom = e.currentTarget.value
                      changer({
                        nom,
                        ...(!edition.id && (!c.slug || c.slug === slugDe(c.nom))
                          ? { slug: slugDe(nom) }
                          : {}),
                      })
                    }}
                    placeholder="Ex. Espaces et équipements"
                  />
                </label>
                <label>
                  Adresse du catalogue
                  <div class="cat-adresse">
                    <span>/catalogue/</span>
                    <input
                      value={c.slug}
                      maxLength={100}
                      disabled={!modifiable}
                      onInput={(e) => changer({ slug: e.currentTarget.value })}
                      placeholder="espaces-et-equipements"
                      aria-label="Adresse du catalogue"
                    />
                  </div>
                  <small>
                    Des lettres minuscules, chiffres et tirets. Une modification
                    change le lien public après publication.
                  </small>
                </label>
                <label class="cat-checkbox">
                  <input
                    type="checkbox"
                    checked={c.par_defaut}
                    disabled={!modifiable}
                    onChange={(e) =>
                      changer({ par_defaut: e.currentTarget.checked })
                    }
                  />
                  <span>
                    <strong>Utiliser comme page d’accueil</strong>
                    <small>
                      Ce catalogue s’affichera à l’entrée de votre site.
                    </small>
                  </span>
                </label>
                {edition.url_publique && (
                  <a
                    class="cat-lien-public"
                    href={`${baseUrl}${edition.url_publique}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Voir le catalogue actuellement publié ↗
                  </a>
                )}
              </div>
              <div class="cat-publier">
                <span class="cat-badge">
                  {edition.publie ? 'Catalogue publié' : 'Pas encore publié'}
                </span>
                <h3>
                  {manques.length
                    ? 'Encore quelques détails.'
                    : 'Votre catalogue est prêt.'}
                </h3>
                <p>
                  {c.sections.length} catégories · {nbArticles} articles ·
                  Présentation en{' '}
                  {c.disposition === 'cartes' ? 'cartes' : 'liste'}
                </p>
                {manques.length ? (
                  <ul class="cat-manques">
                    {manques.map((m) => (
                      <li>{m}</li>
                    ))}
                  </ul>
                ) : (
                  <p class="cat-pret">
                    Chaque article est associé à un parcours publié.
                  </p>
                )}
                <button
                  disabled={occupe || !c.nom.trim() || !c.slug}
                  onClick={() => void voirApercu()}
                >
                  Vérifier l’aperçu
                </button>
                {modifiable && (
                  <button
                    class="cat-primaire"
                    disabled={occupe || manques.length > 0}
                    onClick={() =>
                      setConfirmation({
                        titre: edition.publie
                          ? 'Publier les modifications ?'
                          : 'Publier ce catalogue ?',
                        message: `Les visiteurs pourront découvrir ${nbArticles} article${nbArticles > 1 ? 's' : ''} dans ${c.sections.length} catégorie${c.sections.length > 1 ? 's' : ''}.${c.par_defaut ? ' Ce catalogue deviendra la page d’accueil du site.' : ''}`,
                        label: 'Publier le catalogue',
                        action: () => void publier(),
                      })
                    }
                  >
                    Publier le catalogue
                  </button>
                )}
                {modifiable && edition.publie && (
                  <button
                    class="cat-supprimer"
                    disabled={occupe}
                    onClick={() =>
                      setConfirmation({
                        titre: 'Retirer ce catalogue du site ?',
                        message:
                          'Le catalogue devient privé. Les articles et leurs parcours restent disponibles aux adresses déjà publiées.',
                        label: 'Retirer du site',
                        danger: true,
                        action: () =>
                          void agir(async () => {
                            const d = (await api(
                              `/${edition.id}/retirer`,
                              'POST',
                              { version_id: edition.version_id },
                            )) as Catalogue
                            setEdition((v) =>
                              v
                                ? { ...v, publie: false, url_publique: null }
                                : v,
                            )
                            await charger()
                            setMessage(
                              'Catalogue retiré du site. Votre brouillon est conservé.',
                            )
                          }),
                      })
                    }
                  >
                    Retirer le catalogue du site
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      ) : null}
      {choix !== null && (
        <dialog
          ref={dialogue}
          class="cat-dialogue cat-choisir-dialogue"
          onCancel={() => setChoix(null)}
        >
          <header>
            <div>
              <p class="cat-surtitre">Inventaire</p>
              <h3>Ajouter des éléments</h3>
            </div>
            <button
              aria-label="Fermer le choix d’articles"
              onClick={() => setChoix(null)}
            >
              ×
            </button>
          </header>
          <label class="cat-recherche">
            <span class="cat-sr">Rechercher dans l’inventaire</span>
            <input
              autoFocus
              type="search"
              value={filtreItems}
              onInput={(e) => setFiltreItems(e.currentTarget.value)}
              placeholder="Rechercher un élément"
            />
          </label>
          <div class="cat-choix-items">
            {items
              .filter(
                (i) =>
                  i.actif &&
                  !section?.elements.some((e) => e.item_id === i.id) &&
                  `${i.nom} ${i.description}`
                    .toLowerCase()
                    .includes(filtreItems.toLowerCase()),
              )
              .map((i) => (
                <label class="cat-choix-item" key={i.id}>
                  <input
                    type="checkbox"
                    checked={choix.includes(i.id)}
                    onChange={(e) =>
                      setChoix(
                        e.currentTarget.checked
                          ? [...choix, i.id]
                          : choix.filter((id) => id !== i.id),
                      )
                    }
                  />
                  <span>
                    <strong>{i.nom}</strong>
                    <small>
                      {typeNom[i.type]} ·{' '}
                      {i.prix_cents === 0
                        ? 'Gratuit'
                        : argent(i.prix_cents) +
                          ' / ' +
                          (unite[i.unite] ?? i.unite)}
                    </small>
                    <small class={i.parcours.length ? '' : 'cat-texte-erreur'}>
                      {i.parcours.length
                        ? `${i.parcours.length} parcours publié${i.parcours.length > 1 ? 's' : ''}`
                        : 'Parcours à créer ou à publier'}
                    </small>
                  </span>
                </label>
              ))}
            {items.filter(
              (i) =>
                i.actif &&
                !section?.elements.some((e) => e.item_id === i.id) &&
                `${i.nom} ${i.description}`
                  .toLowerCase()
                  .includes(filtreItems.toLowerCase()),
            ).length === 0 && (
              <div class="cat-vide cat-vide-petit">
                <p>
                  {filtreItems
                    ? 'Aucun élément ne correspond.'
                    : 'Tous vos éléments disponibles sont déjà dans cette catégorie.'}
                </p>
                <a href="#inventaire" onClick={() => setChoix(null)}>
                  Ouvrir l’inventaire →
                </a>
              </div>
            )}
          </div>
          <footer>
            <span>
              {choix.length} sélectionné{choix.length > 1 ? 's' : ''}
            </span>
            <button
              class="cat-primaire"
              disabled={!choix.length}
              onClick={ajouterChoix}
            >
              Ajouter {choix.length || ''} article{choix.length > 1 ? 's' : ''}
            </button>
          </footer>
        </dialog>
      )}
      {apercu && (
        <dialog
          ref={dialogueApercu}
          class="cat-dialogue cat-apercu-dialogue"
          onCancel={() => setApercu(null)}
        >
          <header>
            <h3>Aperçu du catalogue</h3>
            <button onClick={() => setApercu(null)}>Fermer</button>
          </header>
          <iframe
            title="Aperçu du catalogue client"
            srcDoc={apercu}
            sandbox="allow-scripts"
          />
        </dialog>
      )}
      {confirmation && (
        <dialog
          ref={dialogueConfirmation}
          class="cat-dialogue cat-confirmation"
          onCancel={() => setConfirmation(null)}
        >
          <h3>{confirmation.titre}</h3>
          <p>{confirmation.message}</p>
          <footer>
            <button onClick={() => setConfirmation(null)}>Annuler</button>
            <button
              class={confirmation.danger ? 'cat-danger' : 'cat-primaire'}
              onClick={() => {
                const action = confirmation.action
                setConfirmation(null)
                action()
              }}
            >
              {confirmation.label}
            </button>
          </footer>
        </dialog>
      )}
    </div>
  )
}

export const stylesCatalogues = `
.cat-admin input[type=checkbox],.cat-admin input[type=radio]{min-height:0!important;min-width:0!important}
.cat-admin{--cat-accent:#391d44;--cat-ligne:#e8e2ea;--cat-muted:#7e6986;min-width:0;color:#2a1632}.cat-admin *{box-sizing:border-box}.cat-admin button,.cat-admin input,.cat-admin select,.cat-admin textarea{font:inherit}.cat-admin button{cursor:pointer;border:1px solid #e2dbe5;background:#fff;border-radius:10px;padding:10px 14px;font-size:13px;font-weight:600;color:#391d44;min-height:40px}.cat-admin button:hover:not(:disabled){background:#f8f4f9;border-color:#ccc0d1}.cat-admin button:disabled{opacity:.45;cursor:default}.cat-admin :focus-visible{outline:3px solid #391d44;outline-offset:3px}.cat-admin button.cat-primaire{background:#391d44;color:#fff;border-color:#391d44}.cat-admin button.cat-primaire:hover:not(:disabled){background:#2a1632}.cat-admin button.cat-danger{background:#9f3c2e;color:#fff;border-color:#9f3c2e}.cat-admin h2{font-size:28px;line-height:1.2;letter-spacing:-.035em;margin:0 0 10px}.cat-admin h3{font-size:17px;margin:0 0 6px;line-height:1.4;letter-spacing:-.02em}.cat-admin p{color:var(--cat-muted);font-size:14px;line-height:1.6}.cat-admin label:not(.cat-checkbox):not(.cat-choix-item):not(.cat-recherche){display:block;font-size:13px;font-weight:600}.cat-admin input:not([type=checkbox]):not([type=radio]):not([type=color]),.cat-admin select,.cat-admin textarea{display:block;width:100%;min-width:0;min-height:43px;background:#fff;color:#2a1632;border:1px solid #dad1dd;border-radius:9px;padding:10px 12px;font-size:14px;line-height:1.4;margin-top:7px}.cat-admin textarea{resize:vertical}.cat-admin small{display:block;color:var(--cat-muted);font-size:12px;font-weight:400;line-height:1.5}.cat-admin label>small{margin-top:7px}.cat-admin label>span{font-weight:400;color:var(--cat-muted)}.cat-admin input[type=checkbox],.cat-admin input[type=radio]{accent-color:#391d44;width:17px;height:17px;flex-shrink:0}.cat-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}.cat-entete,.cat-edition-entete{display:flex;justify-content:space-between;align-items:center;gap:24px;margin-bottom:26px}.cat-entete p,.cat-edition-entete p{margin:0}.cat-admin .cat-surtitre{font-size:11px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#6b5a70;margin:0 0 12px}.cat-entete>button{flex-shrink:0}.cat-notice{display:flex;justify-content:space-between;gap:12px;padding:14px 18px;background:#f6f1f8;border:1px solid #dccde6;border-radius:12px;margin-bottom:20px;font-size:14px}.cat-erreur{background:#fff1e9;border-color:#e7ba9e;color:#804a2f}.cat-notice button{min-height:0;padding:0 5px;border:0;background:transparent}.cat-barre{display:flex;justify-content:space-between;align-items:center;gap:20px;margin:24px 0;color:var(--cat-muted);font-size:13px}.cat-recherche{max-width:400px;flex:1}.cat-admin .cat-recherche input{margin:0}.cat-cartes{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px}.cat-admin button.cat-carte{text-align:left;padding:0;overflow:hidden;border-radius:16px;box-shadow:0 2px 6px #391d4405}.cat-miniature{background:color-mix(in srgb,var(--cat-couleur) 8%,#fff);height:150px;padding:25px 35px;display:flex;flex-direction:column;gap:16px}.cat-miniature>span{height:10px;background:var(--cat-couleur);width:52%;border-radius:3px;opacity:.65}.cat-miniature>div{display:flex;gap:12px;flex:1}.cat-miniature i{background:#fff;width:33%;border-radius:6px;border:1px solid #e7e1e9;box-shadow:0 3px 4px #391d4405}.cat-carte-info{padding:22px}.cat-etiquettes{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:14px}.cat-badge{display:inline-flex;align-items:center;font-size:10px;line-height:1.4;font-weight:650;border:1px solid #e7e1ea;background:#faf7fb;border-radius:5px;padding:4px 7px;color:#7a6682;white-space:nowrap}.cat-publie{color:#326344;background:#edf5ea;border-color:#cfe2ca}.cat-carte-info h3{font-size:19px}.cat-carte-info p{margin:7px 0 23px;font-size:13px}.cat-ouvrir{display:flex;justify-content:space-between;font-size:13px}.cat-vide{text-align:center;background:#fff;border:1px dashed #dad1de;border-radius:16px;padding:55px 30px}.cat-vide p{max-width:450px;margin:9px auto 22px}.cat-vide-icone{display:inline-grid;place-items:center;border-radius:16px;width:58px;height:58px;background:#f6f1f8;color:#796581;font-size:28px;margin-bottom:20px}.cat-vide-petit{padding:32px 20px}.cat-reprendre{padding:15px 18px;background:#f5f0f7;border:1px solid #e6e0e9;border-radius:12px;display:flex;align-items:center;justify-content:space-between;gap:15px;font-size:14px}.cat-actions{display:flex;gap:9px;align-items:center}.cat-admin button.cat-retour{border:0;padding:0;margin:0 0 12px;background:none;font-size:12px;min-height:24px}.cat-onglets{display:flex;gap:25px;border-bottom:1px solid var(--cat-ligne);margin-bottom:26px;overflow-x:auto}.cat-admin .cat-onglets button{border:0;border-radius:0;border-bottom:2px solid transparent;padding:12px 0 16px;background:none;white-space:nowrap;color:#816c8a}.cat-admin .cat-onglets button[aria-current=page]{border-color:#391d44;color:#391d44}.cat-onglets span{display:inline-flex;justify-content:center;align-items:center;border-radius:99px;background:#eee9dd;min-width:18px;height:18px;margin-left:7px;font-size:10px;color:#736344}.cat-composition{display:grid;grid-template-columns:270px minmax(0,1fr);gap:25px}.cat-categories{border:1px solid var(--cat-ligne);border-radius:14px;background:#fbf9fc;align-self:start;overflow:hidden}.cat-panneau-titre{margin-bottom:23px}.cat-panneau-titre p{font-size:13px;margin:7px 0 0}.cat-categories .cat-panneau-titre{padding:22px 19px 0}.cat-categories h3{font-size:14px}.cat-categories .cat-panneau-titre p{font-size:12px}.cat-section-liste{padding:0 10px}.cat-section-choix{display:flex;align-items:center;border:1px solid transparent;border-radius:10px;min-width:0}.cat-section-choix.active{background:#fff;border-color:#ddd5e0;box-shadow:0 2px 3px #391d4404}.cat-admin .cat-section-ouvrir{display:flex;gap:10px;align-items:center;text-align:left;border:0;background:none;flex:1;min-width:0;padding:12px 10px}.cat-section-ouvrir>span:first-child{font-size:17px;color:#6b5a70}.cat-section-ouvrir>span:nth-child(2){overflow-wrap:anywhere;font-size:13px}.cat-section-ouvrir small{font-size:11px;margin-top:4px}.cat-deplacer{display:flex;flex-direction:column;padding-right:6px}.cat-admin .cat-deplacer button{border:0;min-height:25px;padding:2px 5px;background:none;font-size:12px}.cat-admin .cat-ajouter-section{width:calc(100% - 24px);margin:16px 12px;border-style:dashed;background:transparent;font-size:12px}.cat-categories .cat-note{font-size:11px;margin:4px 20px 22px}.cat-section-editeur{min-width:0;background:#fff;border:1px solid var(--cat-ligne);border-radius:14px;padding:25px}.cat-section-champs{display:grid;gap:20px}.cat-admin label>span{font-size:11px}.cat-sous-entete{display:flex;align-items:center;justify-content:space-between;gap:18px;margin:32px 0 17px}.cat-sous-entete h3{font-size:15px}.cat-sous-entete p{font-size:12px;margin:5px 0 0}.cat-sous-entete>button{flex-shrink:0;font-size:12px}.cat-items{display:grid;gap:12px}.cat-item{border:1px solid #e7e1e9;border-radius:12px;padding:18px;display:grid;grid-template-columns:minmax(0,1fr) minmax(190px,1.05fr) auto;gap:18px;align-items:center;background:#fefefe}.cat-item h4{font-size:15px;line-height:1.4;margin:9px 0 5px;overflow-wrap:anywhere}.cat-item p{font-size:12px;margin:0}.cat-admin .cat-parcours{font-size:11px}.cat-admin .cat-parcours select{font-size:12px;min-height:40px}.cat-parcours small{margin-top:6px;font-size:11px}.cat-item-actions{display:grid;gap:3px}.cat-admin .cat-item-actions button{padding:2px 8px;min-height:27px;border:0;background:none}.cat-admin button.cat-supprimer{padding:8px 0;min-height:32px;color:#956c59;font-size:12px;border:0;background:none;margin-top:24px;text-align:left}.cat-texte-erreur{color:#a06039!important}.cat-apparence{display:grid;grid-template-columns:minmax(260px,.78fr) minmax(0,1.3fr);gap:30px;align-items:start}.cat-reglages{background:#fff;border:1px solid var(--cat-ligne);border-radius:14px;padding:26px;display:grid;gap:23px}.cat-reglages .cat-panneau-titre{margin-bottom:0}.cat-couleur{display:flex;align-items:end;gap:22px}.cat-admin input[type=color]{width:49px;height:40px;border:1px solid #e0d8e3;border-radius:9px;padding:4px;display:block;margin-top:10px;background:#fff}.cat-palettes{display:flex;gap:10px;padding-bottom:7px}.cat-admin .cat-palettes button{width:26px;height:26px;min-height:0;padding:0;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px #e1d9e4}.cat-palettes button[aria-pressed=true]{outline:2px solid #695870;outline-offset:3px}.cat-disposition{border:0;padding:0;margin:0;display:grid;grid-template-columns:1fr 1fr;gap:10px}.cat-disposition legend{font-size:13px;font-weight:600;margin-bottom:12px}.cat-admin .cat-disposition label{position:relative;border:1px solid #e5dee7;border-radius:10px;padding:13px;font-weight:400}.cat-disposition label:has(input:checked){background:#f7f3f9;border-color:#c1b2c7}.cat-disposition input{position:absolute;right:9px;top:10px}.cat-disposition label>span{display:block;font-size:24px;color:#6b5a70;margin-bottom:10px}.cat-disposition strong{font-size:12px;display:block}.cat-disposition small{font-size:10px;margin-top:5px}.cat-checkbox{display:flex;gap:12px;align-items:start;font-size:13px;line-height:1.6}.cat-checkbox input{margin-top:3px}.cat-admin .cat-checkbox>span{font-size:13px;font-weight:400;color:#4e4153}.cat-checkbox small{margin-top:5px}.cat-preview-wrap{border:1px solid #e1d9e4;border-radius:13px;overflow:hidden;box-shadow:0 4px 20px #391d4408}.cat-preview-entete{background:#f5eff7;border-bottom:1px solid #e4dde6;display:flex;justify-content:space-between;align-items:center;padding:10px 16px;gap:12px;color:#836e8c;font-size:11px}.cat-admin .cat-preview-entete button{font-size:11px;padding:4px 8px;min-height:28px;background:none;border:0}.cat-preview{padding:26px;background:#faf7fb;color:#2a1632;max-height:960px;overflow:auto}.cat-preview-marque{font-size:11px;font-weight:650;display:block;border-bottom:1px solid #e8e3ea;padding-bottom:20px;margin-bottom:32px}.cat-admin .cat-preview-sur{font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--cat-couleur);font-weight:650;margin:0 0 13px}.cat-preview h3{font-size:29px;line-height:1.15;letter-spacing:-.04em}.cat-preview>p:not(.cat-preview-sur){font-size:12px;white-space:pre-line}.cat-preview-menu{display:flex;flex-wrap:wrap;gap:5px;margin:22px 0 13px}.cat-preview-menu span{font-size:9px;line-height:1.4;border-radius:99px;background:#fff;border:1px solid #e5dfe8;padding:6px 10px}.cat-preview-recherche{font-size:10px;border:1px solid #e2dae4;background:#fff;border-radius:7px;padding:9px 11px;color:#6b5a70;margin-bottom:22px}.cat-preview section{margin-top:20px}.cat-preview h4{font-size:16px;margin:0 0 12px;letter-spacing:-.02em}.cat-preview section>p{font-size:10px}.cat-preview-items{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.cat-preview-items article{background:#fff;border:1px solid #e8e2ea;border-radius:11px;padding:14px;min-width:0;display:flex;flex-direction:column}.cat-preview-items small{font-size:8px;color:var(--cat-couleur)}.cat-preview h5{font-size:13px;margin:13px 0 6px}.cat-preview article p{font-size:9px;margin:0 0 13px;max-height:55px;overflow:hidden;line-height:1.5}.cat-preview article strong{font-size:11px;margin-top:auto}.cat-preview article>span{font-size:9px;color:var(--cat-couleur);font-weight:650;margin-top:12px;border-top:1px solid #efe6f2;padding-top:10px}.cat-preview-liste{grid-template-columns:1fr}.cat-preview-liste article{display:grid;grid-template-columns:1fr 120px}.cat-preview-liste small,.cat-preview-liste h5,.cat-preview-liste p{grid-column:1}.cat-preview-liste strong{grid-column:2;grid-row:1/3;align-self:center;text-align:right;margin:0}.cat-preview-liste article>span{grid-column:2;text-align:right;margin:0;border:0}.cat-preview-placeholder{grid-column:1/-1;font-size:11px;border:1px dashed #dcd3df;padding:24px;border-radius:10px;text-align:center;color:#6b5a70}.cat-publication{display:grid;grid-template-columns:minmax(0,1fr) minmax(280px,.9fr);gap:30px;align-items:start;max-width:1000px}.cat-adresse{display:flex;align-items:center;margin-top:7px;border:1px solid #dad1dd;border-radius:9px;overflow:hidden}.cat-adresse>span{font-size:12px;color:#6b5a70;padding-left:12px;white-space:nowrap}.cat-admin .cat-adresse input{border:0;margin:0;padding-left:4px;min-width:0}.cat-lien-public{font-size:13px;color:#391d44;overflow-wrap:anywhere}.cat-publier{background:#faf7fb;border:1px solid #e7dde9;border-radius:16px;padding:29px}.cat-publier h3{font-size:23px;margin-top:23px}.cat-publier>p{font-size:13px}.cat-publier>button:not(.cat-supprimer){display:block;width:100%;margin-top:12px}.cat-manques{padding-left:19px;color:#8b6344;font-size:13px;line-height:1.65;margin:22px 0}.cat-manques li{margin:9px 0}.cat-admin .cat-pret{border-top:1px solid #e7dde9;padding-top:20px;margin:23px 0;color:#466d44}.cat-dialogue{border:1px solid #e2dbe5;border-radius:18px;padding:26px;background:#fff;color:#2a1632;box-shadow:0 25px 100px #2a163238;max-width:calc(100vw - 32px);max-height:calc(100dvh - 36px);overflow:auto}.cat-dialogue::backdrop{background:#2a163255;backdrop-filter:blur(3px)}.cat-dialogue header,.cat-dialogue footer{display:flex;justify-content:space-between;align-items:center;gap:18px}.cat-dialogue header{margin-bottom:20px}.cat-dialogue header .cat-surtitre{margin-bottom:6px}.cat-dialogue h3{font-size:22px;line-height:1.3;margin:0}.cat-dialogue footer{padding-top:20px;margin-top:18px;border-top:1px solid #eae5ec;font-size:12px;color:#6b5a70}.cat-choisir-dialogue{width:640px}.cat-choisir-dialogue .cat-recherche{display:block;max-width:none}.cat-choix-items{max-height:52vh;overflow:auto;margin-top:18px}.cat-choix-item{display:flex;align-items:center;gap:14px;border-bottom:1px solid #f1e9f4;padding:15px 8px;cursor:pointer}.cat-choix-item:has(input:checked){background:#f7f3f9}.cat-choix-item strong{display:block;font-size:14px;font-weight:600;margin-bottom:3px;color:#2a1632}.cat-choix-item small{font-size:11px;line-height:1.7}.cat-apercu-dialogue{width:1220px;padding:0;height:calc(100dvh - 50px)}.cat-apercu-dialogue header{margin:0;padding:16px 23px;border-bottom:1px solid #e7dde9}.cat-apercu-dialogue h3{font-size:16px}.cat-apercu-dialogue iframe{width:100%;height:calc(100% - 74px);border:0;display:block}.cat-confirmation{width:460px;padding:29px}.cat-confirmation p{font-size:14px;color:#7a6682;line-height:1.6}.cat-confirmation footer{border:0;margin-top:25px;padding:0;justify-content:flex-end}
@media(max-width:1200px){.cat-cartes{grid-template-columns:repeat(2,minmax(0,1fr))}.cat-composition{grid-template-columns:220px minmax(0,1fr);gap:18px}.cat-item{grid-template-columns:minmax(0,1fr) auto;gap:12px}.cat-parcours{grid-column:1}.cat-item-actions{grid-column:2;grid-row:1/3}.cat-apparence{gap:20px}.cat-preview{padding:20px}.cat-preview-items{grid-template-columns:1fr}.cat-preview h3{font-size:26px}}
@media(max-width:800px){.cat-entete,.cat-edition-entete{align-items:flex-start;gap:16px;flex-wrap:wrap}.cat-admin h2{font-size:24px}.cat-composition,.cat-apparence,.cat-publication{grid-template-columns:1fr}.cat-categories{width:100%}.cat-section-liste{display:flex;gap:7px;overflow:auto}.cat-section-choix{flex-shrink:0;min-width:170px;max-width:240px}.cat-categories .cat-note{display:none}.cat-categories .cat-panneau-titre{padding-top:16px;margin-bottom:14px}.cat-admin .cat-ajouter-section{margin-top:12px;width:auto;min-width:180px}.cat-preview-wrap{max-width:700px}.cat-preview-items{grid-template-columns:repeat(2,minmax(0,1fr))}.cat-preview-liste{grid-template-columns:1fr}.cat-section-editeur{padding:20px}.cat-reglages{padding:23px}.cat-item{grid-template-columns:1fr minmax(180px,1fr) auto}.cat-parcours{grid-column:auto}.cat-item-actions{grid-column:auto;grid-row:auto}}
@media(max-width:520px){.cat-cartes{grid-template-columns:1fr}.cat-entete>button{width:100%}.cat-barre{gap:12px}.cat-barre>span{white-space:nowrap;font-size:11px}.cat-actions{width:100%}.cat-actions>button{flex:1}.cat-onglets{gap:19px;margin-bottom:20px}.cat-admin .cat-onglets button{font-size:12px}.cat-sous-entete{display:block}.cat-sous-entete>button{margin-top:13px;width:100%}.cat-item{grid-template-columns:minmax(0,1fr) auto;padding:16px}.cat-parcours{grid-column:1}.cat-item-actions{grid-column:2;grid-row:1/3}.cat-section-editeur,.cat-reglages{padding:18px}.cat-publier{padding:23px}.cat-preview{padding:19px}.cat-preview-items{grid-template-columns:1fr}.cat-reprendre{align-items:flex-start;flex-direction:column}.cat-dialogue{padding:20px;max-width:calc(100vw - 20px)}.cat-dialogue h3{font-size:20px}.cat-apercu-dialogue{padding:0}.cat-apercu-dialogue h3{font-size:15px}.cat-dialogue footer{gap:10px}.cat-confirmation footer{flex-direction:column-reverse}.cat-confirmation footer button{width:100%}.cat-palettes{gap:7px}.cat-couleur{gap:16px}}
`
